import moment from 'moment'
import { AdmissionPartner, AdmissionProperties, AdmissionUser, RelationArea, RelationHospital } from '@/infrastructure/network/Monitaro/schema/models'

export interface IAdmissionProps {
  id?: string
  hospitalId?: string | null
  userId?: string | null
  areaId?: string | null
  user?: AdmissionUser | null
  hospital?: RelationHospital | null
  area?: RelationArea | null
  staytime: number | null
  properties: AdmissionProperties
  startedAt: string | null
  isAppointment: boolean | null
  hasResult: boolean | null
  checkinAt: string | null
  checkoutAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface IAdmissionQueryProps {
  started: string | null
  ended: string | null
  staytime: { duration: string | null; range: string } | null
  status: string | null
  areas: string[] | null
}

export default class AdmissionEntity {
  private _props: IAdmissionProps

  constructor(props?: IAdmissionProps | null) {
    this._props = EmptyAdmissionPropsFactory(props)
  }

  get props(): IAdmissionProps {
    return this._props
  }

  get clone(): IAdmissionProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get hospitalId(): string | null | undefined {
    return this._props.hospitalId
  }

  get userId(): string | null | undefined {
    return this._props.userId
  }

  get areaId(): string | null | undefined {
    return this._props.areaId
  }

  get user(): AdmissionUser | null | undefined {
    return this._props.user
  }

  get hospital(): RelationHospital | null | undefined {
    return this._props.hospital
  }

  get area(): RelationArea | null | undefined {
    return this._props.area
  }

  get staytime(): string {
    if (this._props.staytime) {
      const duration = moment.duration(this._props.staytime, 'seconds')

      const hours = duration.hours()
      const hour = hours > 0 ? `${duration.hours()}時間` : ''

      const minutes = duration.minutes()
      const minute = minutes > 0 ? `${duration.minutes()}分` : '0分'

      return `${hour}${minute}`
    }
    return '-'
  }

  get properties(): AdmissionProperties | null | undefined {
    return this._props.properties
  }

  get destination(): string {
    return this._props.properties?.destination || ''
  }

  get purpose(): string {
    return this._props.properties?.purpose || ''
  }

  get tag(): string | null {
    return this._props.properties?.tag || null
  }

  get temperature(): string | null {
    return this._props.properties?.temperature || null
  }

  get health(): boolean {
    return (this._props.area?.flags?.health && this._props.properties?.health) || false
  }

  get appointment(): { text: string; value: string; color: string; textColor: string } | null {
    if (!this._props.startedAt) {
      return null
    }
    if (this._props.isAppointment) {
      return { text: '承認済', value: 'approved', color: 'm-blue', textColor: 'white' }
    }
    return { text: '申告済', value: 'declared', color: 'm-green', textColor: 'white' }
  }

  get hasResult(): boolean {
    return !!this._props.hasResult
  }

  get checkin(): string | null {
    return moment(this._props.checkinAt).format('H:mm')
  }

  get checkout(): string | null {
    return this._props.checkoutAt ? moment(this._props.checkoutAt).format('H:mm') : '-'
  }

  get createdAt(): string | null {
    return this._props.createdAt
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get year(): string | null {
    const year = this._props.checkinAt ? moment(this._props.checkinAt).format('YYYY') : null
    return moment().year().toString() === year ? null : year
  }

  get date(): string | null {
    return this._props.checkinAt ? moment(this._props.checkinAt).format('M/D') : null
  }

  get checkinOn(): string | null {
    const year = this._props.checkinAt ? moment(this._props.checkinAt).format('YYYY') : null
    return year + ' ' + this.date
  }

  get partners(): AdmissionPartner[] {
    return this._props.properties?.partners || []
  }
}

export const headers = [
  { text: '日付', value: 'date' },
  { text: '名前/会社名', value: 'user' },
  { text: '場所', value: 'area' },
  { text: 'IN', value: 'checkin', class: 'text-right' },
  { text: 'OUT', value: 'checkout', class: 'text-right' },
  { text: '滞在時間', value: 'staytime', class: 'text-right' },
  { text: '訪問先/面談目的', value: 'destination', class: 'text-center' },
  { text: '入館番号/同行者', value: 'tag', class: 'text-center' },
  { text: '健康', value: 'temperature' },
  { text: 'アポ', value: 'appointment' }
]

export const EmptyAdmissionPropsFactory = (props?: Partial<IAdmissionProps> | null): IAdmissionProps => ({
  staytime: null,
  startedAt: null,
  isAppointment: null,
  hasResult: null,
  checkinAt: null,
  checkoutAt: null,
  createdAt: null,
  updatedAt: null,
  ...props,
  properties: EmptyAdmissionPropertiesFactory(props?.properties)
})

export const EmptyAdmissionPropertiesFactory = (props?: Partial<AdmissionProperties> | null): AdmissionProperties => ({
  tag: null,
  destination: null,
  purpose: null,
  temperature: null,
  partners: props?.partners?.map((prop) => EmptyAdmissionPartnerFactory(prop)) || [],
  ...props
})

export const EmptyAdmissionPartnerFactory = (props?: Partial<AdmissionPartner> | null): AdmissionPartner => ({
  name: null,
  temperature: null,
  ...props
})

export const EmptyAdmissionQueryPropsFactory = (props?: Partial<IAdmissionQueryProps> | null): IAdmissionQueryProps => ({
  started: null,
  ended: null,
  staytime: { duration: null, range: '>=' },
  status: null,
  areas: null,
  ...props
})
